import { Modal, Button, Icon } from '@distribusion/owl-frontend-ui'
import React from 'react'

import useLogout from '@hooks/useLogout'
import emailConfirmImg from '@images/accounts/email-confirm.png'
import bem from '@lib/bem'
import { Trans, useTranslation } from '@lib/i18n'
import { ModalType, MODAL_TYPES } from '@pages/Account/hooks/usePasswordChange'

import '@pages/Account/Settings/Modals/ConfirmationModal/index.scss'

interface ConfirmationModalProps {
  isOpen: boolean
  onClose: () => void
  email?: string | null
  type?: ModalType | null
}

export const ConfirmationModal = ({
  isOpen,
  onClose,
  email,
  type = MODAL_TYPES.CHANGE_EMAIL,
}: ConfirmationModalProps) => {
  const { t } = useTranslation()
  const { logout } = useLogout()

  const isPasswordConfirmation = type === MODAL_TYPES.CHANGE_PASSWORD
  const isForgotPasswordConfirmation = type === MODAL_TYPES.FORGOT_PASSWORD
  const isResetPasswordConfirmation = type === MODAL_TYPES.RESET_PASSWORD

  const handleClose = () => {
    if (isPasswordConfirmation) {
      logout()
    }
    onClose()
  }

  const getModalContent = () => {
    if (isPasswordConfirmation) {
      return {
        image: <Icon name="checkout" size="large" />,
        title: t('account.settings.signIn.passwordConfirmModal.title'),
        description: t('account.settings.signIn.passwordConfirmModal.description'),
        buttonText: t('account.settings.signIn.passwordConfirmModal.button'),
      }
    }

    if (isForgotPasswordConfirmation) {
      return {
        image: <img src={emailConfirmImg} alt="Email confirmation icon" />,
        title: t('account.passwordRecovery.checkEmailModal.title'),
        description: (
          <Trans
            i18nKey="account.passwordRecovery.checkEmailModal.description"
            values={{ email }}
            components={[<span key="" className={bem('modal-content', 'email')} />]}
          />
        ),
        buttonText: t('account.passwordRecovery.checkEmailModal.button'),
      }
    }

    if (isResetPasswordConfirmation) {
      return {
        image: <Icon name="checkout" size="large" />,
        title: t('account.passwordRecovery.resetConfirmModal.title'),
        description: t('account.passwordRecovery.resetConfirmModal.description'),
        buttonText: t('account.passwordRecovery.resetConfirmModal.button'),
      }
    }

    return {
      image: <img src={emailConfirmImg} alt="Email confirmation icon" />,
      title: t('account.settings.signIn.emailConfirmModal.title'),
      description: (
        <Trans
          i18nKey="account.settings.signIn.emailConfirmModal.description"
          values={{ email }}
          components={[<span key="" className={bem('modal-content', 'email')} />]}
        />
      ),
      buttonText: t('account.settings.signIn.emailConfirmModal.button'),
    }
  }

  const content = getModalContent()

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      title={
        <div className="modal-title">
          <div>{content.image}</div>
          <span> {content.title}</span>
        </div>
      }
      maxWidth={isPasswordConfirmation || isResetPasswordConfirmation ? 'md' : 'sm'}
      className={bem('confirmation', 'modal')}
      dataTag="confirmation-modal"
    >
      <div className="modal-content">
        <span>{content.description}</span>
        <div className={bem('modal-content', 'button')}>
          <Button onClick={handleClose} className="confirm-btn" dataTag="confirmation-modal-close-button">
            {content.buttonText}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
